import { pageTitle } from '@palaccount/config';
import type { PurchaseReturnReason } from '@palaccount/contracts';
import { MAX_PAGE_SIZE } from '@palaccount/contracts';
import { Loader2, Send, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { DocumentNumberChip } from '@/components/document-number-chip';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { PurchaseReturnStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useCashAccountOptions } from '@/features/accounts/api';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { formatQty } from '@/lib/format';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { FxPanel, useRateWarningGate } from '@/features/documents/fx-panel';
import { RateField, useRateInput } from '@/features/documents/rate-field';
import { purchaseReturnDocument } from '@/features/documents/record-documents';
import { Typeahead } from '@/components/ui/typeahead';
import { useItemsHeld } from '@/features/inventory/api';
import { useWarehouses } from '@/features/inventory/stock-api';
import { SupplierBalanceChip } from '@/features/partners/supplier-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import {
  captureBlocks,
  textFromStored,
  trackingPayload,
  type CaptureText,
  type TrackedItem,
} from '@/features/sales/tracking-capture';
import { TrackingRows } from '@/features/sales/tracking-rows';

import { useBill, useBillPicker } from './api';
import {
  fitsCaps,
  isNotReturned,
  isPurchaseReturnEditable,
  remainingQty,
  returnCeiling,
  shelfIsTheLimit,
  toReturnLines,
  trimQty,
} from './return-caps';
import { useOrgSetting } from '@/features/admin/api';
import { computeTotalsMirror } from '@/features/sales/totals-mirror';
import { DirectPurchaseReturnEditor } from './return-direct-editor';
import {
  EMPTY_RETURN_HEADER,
  ReturnHeaderFields,
  type ReturnHeaderValues,
} from './return-header-fields';
import {
  useCreatePurchaseReturn,
  useCancelPurchaseReturn,
  useDeletePurchaseReturn,
  usePostPurchaseReturn,
  useRefundPurchaseReturn,
  usePurchaseReturn,
  usePurchaseReturns,
  useUpdatePurchaseReturn,
} from './returns-api';

/**
 * PUR-04 §5 field 5: "the same five-value list the details line uses" —
 * SAL-00 §5d field 14, shared with the sales return and not forked here.
 */
const REASONS: PurchaseReturnReason[] = [
  'damaged_goods',
  'wrong_item_shipped',
  'customer_changed_mind',
  'overstock',
  'other',
];

interface ReturnRow {
  billLineNo: number;
  itemId: string | null;
  itemCode: string | null;
  description: string;
  billedQty: string;
  alreadyReturnedQty: string;
  onHandQty: string;
  /**
   * `N-4` — the unit all three caps are in. A bill line carries no unit of
   * its own (`purchase_invoice_lines` has no `unit_id`), so it is the item's
   * stock unit, which is also the unit `onHandQty` is a balance in — the
   * reason the three figures can be compared at all. Null on a free-text or
   * service line, which has no item and no unit of measure.
   */
  unitSymbol: string | null;
  unitPrecision: number | null;
  warehouseName: string;
  /** Wave 5D — where THIS line's goods leave from; blank = where they came in. */
  warehouseId: string;
  entryUnitCost: string;
  unitPrice: string;
  discountPct: string;
  qty: string;
  /**
   * BR-MD-03-17 on the way BACK OUT. The units were named at the goods
   * receipt; a serial that leaves unnamed stays on the shelf in the ledger
   * for ever, so the same sub-table the receiving side used sits under the
   * row.
   */
  tracking: CaptureText;
}

/**
 * One cap figure, with the unit when the row has one. A free-text or service
 * line has no item and therefore no unit of measure — nothing to name and no
 * precision to round to — so it prints the bare figure, which is the honest
 * answer `formatQty`'s required parameter forces somebody to give.
 */
function CapQty({
  unit,
  value,
}: {
  unit: { symbol: string; precision: number } | null;
  value: string;
}) {
  if (unit === null) {
    return (
      <bdi dir="ltr" className="font-mono tabular-nums">
        {trimQty(value)}
      </bdi>
    );
  }
  return <Qty value={value} precision={unit.precision} unit={unit.symbol} />;
}

export function CellInput({
  mono,
  className,
  ...props
}: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * PUR-04 — the Purchase Return (Debit Note) editor, reached from a POSTED
 * bill's own screen ("Debit this bill" — the bill arrives already linked with
 * its lines loaded, §3) or from the returns list. It is its OWN screen on its
 * own route with no document-type selector (owner ruling R3, §16).
 *
 * What the caller controls is exactly what the spec allows: WHICH lines and
 * HOW MANY go back, capped twice — by billed minus already returned
 * (BR-PUR-04-04) and by what is on the shelf (BR-PUR-04-05) — the reason, the
 * RESTOCKED flag and the value-only switch. Prices and rates are the bill's
 * own, drawn read-only (BR-PUR-04-06).
 *
 * THE RESTOCKED FLAG IS ON SCREEN, which is the whole of BR-PUR-04-08: "the
 * flag the screen does not show … that single fact decides whether 8,900
 * stays in inventory or becomes a loss." It is a labelled control with the
 * accounting consequence written beside it, because the consequence is the
 * only reason anybody would touch it.
 *
 * Post return (purchasing.approve) writes the supplier_return movements and
 * the §12 entry; a posted debit note is locked except its notes (R8).
 */
export function PurchaseReturnEditorPage() {
  const { returnId } = useParams<{ returnId: string }>();
  const [searchParams] = useSearchParams();
  const { data: record, isPending } = usePurchaseReturn(returnId);

  // Wave 5D — the route decides which of the debit note's two shapes to
  // draw, exactly as the sales return's does: a NEW return with
  // `?mode=direct` is the DIRECT editor (a supplier and free lines, no bill);
  // any other new return is the bill-linked editor, whose picker offers the
  // direct door too. A SAVED return decides once it is read.
  if (!returnId) {
    return searchParams.get('mode') === 'direct' ? (
      <DirectPurchaseReturnEditor returnId={undefined} />
    ) : (
      <BillLinkedReturnEditor />
    );
  }
  if (isPending || !record) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }
  return record.isStandalone ? (
    <DirectPurchaseReturnEditor returnId={returnId} />
  ) : (
    <BillLinkedReturnEditor />
  );
}

/** The bill-linked shape — every line one billed line, capped and copied. */
function BillLinkedReturnEditor() {
  const { t, i18n } = useTranslation();
  // The reader's language is the PDF's; `print.language` may override it in
  // the template (INV-H6), for the worker and the browser alike.
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { returnId } = useParams<{ returnId: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !returnId;
  /**
   * BR-PUR-04-03: "chosen from a picker limited to that supplier's posted
   * bills". "Debit this bill" seeds it from the query string (§3 — the
   * navigation off a posted bill); arriving from the menu entry instead
   * leaves it empty and the picker below is the way in.
   */
  const [pickedSupplierId, setPickedSupplierId] = useState('');
  const [pickedBillId, setPickedBillId] = useState(searchParams.get('billId') ?? '');
  const sourceBillId = pickedBillId === '' ? undefined : pickedBillId;

  const { data: record, isPending: loadingReturn } = usePurchaseReturn(returnId);
  // A NEW return builds its rows from the posted bill + its posted returns.
  const { data: sourceBill } = useBill(isNew ? sourceBillId : undefined);
  /*
   * NOT a list screen: this is "every posted debit note against THIS bill",
   * summed to prefill each line's remaining quantity. It asks for the page
   * ceiling rather than the unbounded `'all'` — one bill carrying 500 posted
   * debit notes does not exist, and BR-PUR-04's cumulative cap is re-checked
   * server-side on post, so a truncation could only ever offer a too-generous
   * default that the server then refuses. `'all'` here would be a new
   * unbounded caller, which `UNPAGED_CALLERS` exists to prevent.
   */
  const { data: priorReturnsPage } = usePurchaseReturns(
    isNew && sourceBillId ? { billId: sourceBillId, status: 'posted' } : {},
    1,
    MAX_PAGE_SIZE,
  );
  const priorReturns = priorReturnsPage?.returns;
  /*
   * GEN-50 — the picker's two searches: suppliers, then THAT supplier's
   * POSTED bills. Never a draft and never a cancelled one (BR-PUR-04-03 /
   * -10), which stays a SERVER predicate: narrowing a page of bills in the
   * browser would show three rows and call them all of them.
   */
  const supplierPicker = usePartnerPicker(pickedSupplierId || null, { type: 'supplier' });
  const billPicker = useBillPicker(
    pickedBillId || null,
    pickedSupplierId ? { partnerId: pickedSupplierId, status: 'posted' } : { status: 'posted' },
  );

  const createReturn = useCreatePurchaseReturn();
  const updateReturn = useUpdatePurchaseReturn(returnId ?? '');
  const deleteReturn = useDeletePurchaseReturn(returnId ?? '');
  const postReturn = usePostPurchaseReturn(returnId ?? '');
  const cancelReturn = useCancelPurchaseReturn(returnId ?? '');
  const refundReturn = useRefundPurchaseReturn(returnId ?? '');
  /** BR-PUR-04-02's deposit target — the §12 row-4 bank/cash account. */
  const { options: cashOptions } = useCashAccountOptions();
  const [refundAccountId, setRefundAccountId] = useState('');

  const canEdit = useCan(isNew ? 'purchasing.create' : 'purchasing.edit');
  const canPost = useCan('purchasing.approve');
  const canEditNotes = useCan('purchasing.edit');

  const [returnDate, setReturnDate] = useDocumentDate();
  const [reason, setReason] = useState<PurchaseReturnReason>('damaged_goods');
  const [reasonNote, setReasonNote] = useState('');
  const [restocked, setRestocked] = useState(false);
  const [valueOnly, setValueOnly] = useState(false);
  /** Wave 5D's header fields: supplier's reference, book no, buyer, returns account. */
  const [header, setHeader] = useState<ReturnHeaderValues>(EMPTY_RETURN_HEADER);
  const [rows, setRows] = useState<ReturnRow[]>([]);
  /** Wave 5D: the per-line "leaves from" select. */
  const { data: warehouses } = useWarehouses();
  const activeWarehouses = (warehouses ?? []).filter((warehouse) => warehouse.isActive);
  /*
   * The catalogue owns an item's tracking (MD-03 panel 7); the debit note
   * carries none of it, and must not. A LOOKUP rather than a picker — the
   * rows come from the bill, so it asks for the items THOSE rows name.
   */
  const { data: items } = useItemsHeld(
    useMemo(() => rows.map((row) => row.itemId), [rows]),
  );
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** GEN-72's ask-why dialog for the posted-note cancel. */
  const [cancelAsk, setCancelAsk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** BR-MD-11-14's question, in ADM-02 §8's shared box (BR-PERM-15). */
  const { confirmRateWarning, rateWarningDialog } = useRateWarningGate();
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [returnDate, reason, reasonNote, restocked, valueOnly, rows, notes],
    isNew ? 'new' : record && loadedId.current === record.id ? record.id : null,
  );
  const seededNew = useRef(false);

  const status = record?.status ?? 'draft';
  const editable = isPurchaseReturnEditable(status) && canEdit;
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  const currency = record?.currency ?? sourceBill?.currency ?? base;
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(record);
  /** The company default VAT rate — the mirror's fallback when neither the note nor the bill names one. */
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');

  useEffect(() => {
    document.title = pageTitle(t('preturn.editorTitle'));
  }, [t]);

  // Existing note: adopt the stored document (both caps ride each line).
  useEffect(() => {
    if (record && loadedId.current !== record.id) {
      loadedId.current = record.id;
      setReturnDate(record.returnDate);
      setReason(record.reason);
      setReasonNote(record.reasonNote ?? '');
      setRestocked(record.restocked);
      setValueOnly(record.valueOnly);
      setNotes(record.notes ?? '');
      setHeader({
        manualRef: record.manualRef ?? '',
        bookNo: record.bookNo ?? '',
        buyerUserId: record.buyerUserId ?? '',
        returnsAccountId: record.returnsAccountId ?? '',
      });
      setRows(
        // A bill-linked note's lines all name a bill line (the route sends a
        // direct note to the other editor); the `?? 0` is the type's, not a
        // case this screen meets.
        record.lines.map((line) => ({
          billLineNo: line.billLineNo ?? 0,
          itemId: line.itemId,
          itemCode: line.itemCode,
          description: line.description,
          billedQty: line.billedQty ?? '0',
          alreadyReturnedQty: line.alreadyReturnedQty ?? '0',
          onHandQty: line.onHandQty,
          unitSymbol: line.unitSymbol,
          unitPrecision: line.unitPrecision,
          warehouseName: line.warehouseName,
          warehouseId: line.warehouseId ?? '',
          entryUnitCost: line.entryUnitCost,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          qty: trimQty(line.qty),
          tracking: textFromStored(line),
        })),
      );
    }
  }, [record]);

  // New note: the bill's lines arrive loaded, quantities defaulting to the
  // full remainder. On-hand is not on the bill, so it arrives blank and the
  // server's own refusal names the shelf — the one cap the editor cannot
  // pre-empt until the draft has been saved once.
  useEffect(() => {
    if (isNew && sourceBill && priorReturns && !seededNew.current) {
      seededNew.current = true;
      const returnedByLine = new Map<number, number>();
      for (const prior of priorReturns) {
        for (const line of prior.lines) {
          if (line.billLineNo === null) continue;
          returnedByLine.set(
            line.billLineNo,
            (returnedByLine.get(line.billLineNo) ?? 0) + Number(line.qty),
          );
        }
      }
      setRows(
        sourceBill.lines.map((line) => {
          const already = String(returnedByLine.get(line.lineNo) ?? 0);
          const remaining = remainingQty(
            { billedQty: line.qty, alreadyReturnedQty: already, onHandQty: '0' },
            sourceBill.currency,
          );
          return {
            billLineNo: line.lineNo,
            itemId: line.itemId,
            itemCode: line.itemCode,
            description: line.description,
            billedQty: line.qty,
            alreadyReturnedQty: already,
            onHandQty: '',
            // No server line yet, so no resolved unit: `unitOf` below reads it
            // off the catalogue at render time instead. Seeding it here would
            // race the catalogue query — this effect runs once, and a row
            // seeded before `items` landed would print bare for ever.
            unitSymbol: null,
            unitPrecision: null,
            warehouseName: '',
            // Wave 5D: the bill line's receiving warehouse is the default;
            // blank = where the goods receipt put the goods.
            warehouseId: line.warehouseId ?? '',
            entryUnitCost: '',
            unitPrice: line.unitPrice,
            discountPct: line.discountPct,
            qty: remaining === '0' ? '' : trimQty(remaining),
            tracking: {},
          };
        }),
      );
    }
  }, [isNew, sourceBill, priorReturns]);

  const setRowQty = (billLineNo: number, qty: string) => {
    setRows((current) =>
      current.map((row) => (row.billLineNo === billLineNo ? { ...row, qty } : row)),
    );
  };

  const setRowWarehouse = (billLineNo: number, warehouseId: string) => {
    setRows((current) =>
      current.map((row) => (row.billLineNo === billLineNo ? { ...row, warehouseId } : row)),
    );
  };

  const setRowTracking = (billLineNo: number, key: string, value: string) => {
    setRows((current) =>
      current.map((row) =>
        row.billLineNo === billLineNo
          ? { ...row, tracking: { ...row.tracking, [key]: value } }
          : row,
      ),
    );
  };

  /**
   * `N-4` — the unit this row's three caps are in. The server's resolution
   * wins where there is one; a row seeded from the bill for a return that has
   * never been saved falls back to the catalogue, which resolves the SAME
   * stock unit by the same rule (`purchase_invoice_lines` has no `unit_id`).
   * A free-text or service line has neither, and prints bare — `formatQty`
   * requires a precision precisely so that case is answered, not defaulted.
   */
  const unitOf = (row: ReturnRow): { symbol: string; precision: number } | null => {
    if (row.unitPrecision !== null) {
      return { symbol: row.unitSymbol ?? '', precision: row.unitPrecision };
    }
    const item = (items ?? []).find((candidate) => candidate.id === row.itemId);
    return item ? { symbol: item.stockUnitSymbol, precision: item.stockUnitPrecision } : null;
  };

  /**
   * What this row must identify going back. `expandKits: false` on purpose: a
   * goods receipt takes a bundle in as ITSELF (PUR-02 never resolves a kit
   * into its parts), so the return sends back the same thing that arrived and
   * asking for a component's serial here would collect identifiers nothing
   * will ever use.
   */
  const blocksFor = (row: ReturnRow) =>
    captureBlocks(
      (items ?? []).find((candidate) => candidate.id === row.itemId) as TrackedItem | undefined,
      row.qty,
      { expandKits: false },
    );

  const requestLines = useMemo(
    () =>
      toReturnLines(
        rows.map((row) => ({
          ...row,
          // §15: a value-only return moves no stock, so there is nothing to
          // name and nothing that could be checked against a shelf.
          tracking: valueOnly ? {} : trackingPayload(blocksFor(row), row.tracking),
        })),
      ),
    // `blocksFor` closes over `items`, which is why it is in the list.
    [rows, items, valueOnly],
  );

  const billCode = record?.billCode ?? sourceBill?.code ?? '';
  const partnerName = record?.partnerName ?? sourceBill?.partnerName ?? '';
  const linkedBillId = record?.billId ?? sourceBillId;

  const validate = (): string | null => {
    if (!linkedBillId) return t('preturn.errPickBill');
    if (requestLines.length === 0) return t('preturn.errNoLines');
    for (const row of rows) {
      // Wave 1C: a blank OR zero row is "not going back" — nothing to check.
      if (isNotReturned(row.qty)) continue;
      const caps = {
        billedQty: row.billedQty,
        alreadyReturnedQty: row.alreadyReturnedQty,
        onHandQty: row.onHandQty === '' ? row.billedQty : row.onHandQty,
      };
      if (fitsCaps(row.qty, caps, { valueOnly, currency })) continue;
      /*
       * §14 has TWO sentences and they are not interchangeable: one tells you
       * to lower it, the other tells you to lower it OR mark it value-only.
       *
       * NO UNIT AND NO PADDING GOES INTO EITHER, and that is `N-4` being
       * deliberately not applied. These are SETTLED sentences quoted word for
       * word (`ERR-PUR-04-02`), and the server composes the same ones from the
       * same figures — the journey asserts "The bill charged for 1 of
       * Industrial resin — bulk". Injecting a unit changes the sentence and
       * reads as part of the item's name; that exact over-reach was made and
       * reverted on `ERR-PUR-06-05`. The COLUMNS beside the box carry the
       * unit, which is where the rule puts it.
       */
      if (!valueOnly && shelfIsTheLimit(row.qty, caps, currency)) {
        return t('preturn.errNotInStock', {
          onHand: trimQty(caps.onHandQty),
          code: row.itemCode ?? row.description,
          warehouse: row.warehouseName || t('preturn.thatWarehouse'),
          wanted: trimQty(row.qty),
        });
      }
      return t('preturn.errQtyCap', {
        billed: trimQty(row.billedQty),
        code: row.itemCode ?? row.description,
        returned: trimQty(row.alreadyReturnedQty),
        left: remainingQty(caps, currency),
      });
    }
    return null;
  };

  const handleFailure = (
    thrown: unknown,
    permission: 'purchasing.create' | 'purchasing.edit' | 'purchasing.delete' | 'purchasing.approve',
  ) => {
    if (thrown instanceof ApiError && thrown.status === 403) denyPermission(permission);
    // The line above already reads the cause to spot a 403; this one used to
    // throw it away one expression later. `localizeServerCause` keeps it: a
    // refusal keeps the server's sentence localized (`PURCHASE_RETURN_MESSAGES`
    // are mapped in STATIC_MESSAGE_KEYS), a request that never answered gets
    // this screen's own sentence rather than the browser's `Failed to fetch`.
    setError(localizeServerCause(t, thrown, 'preturn.errSaveFailed'));
  };

  const save = async (navigateAfter = true): Promise<string | null> => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    try {
      if (isNew) {
        const created = await createReturn.mutateAsync({
          ...rate.createBody(),
          billId: linkedBillId!,
          returnDate,
          reason,
          ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}),
          restocked,
          valueOnly,
          lines: requestLines,
          ...(header.manualRef.trim() ? { manualRef: header.manualRef.trim() } : {}),
          ...(header.bookNo.trim() ? { bookNo: header.bookNo.trim() } : {}),
          ...(header.buyerUserId ? { buyerUserId: header.buyerUserId } : {}),
          ...(header.returnsAccountId ? { returnsAccountId: header.returnsAccountId } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        if (navigateAfter) void navigate(`/purchases/returns/${created.id}`, { replace: true });
        return created.id;
      }
      await updateReturn.mutateAsync({
        ...rate.patchBody(),
        returnDate,
        reason,
        reasonNote: reasonNote.trim() === '' ? null : reasonNote.trim(),
        restocked,
        valueOnly,
        lines: requestLines,
        manualRef: header.manualRef.trim() === '' ? null : header.manualRef.trim(),
        bookNo: header.bookNo.trim() === '' ? null : header.bookNo.trim(),
        buyerUserId: header.buyerUserId === '' ? null : header.buyerUserId,
        returnsAccountId: header.returnsAccountId === '' ? null : header.returnsAccountId,
        notes: notes.trim() === '' ? null : notes,
      });
      loadedId.current = null;
      return returnId!;
    } catch (thrown) {
      handleFailure(thrown, isNew ? 'purchasing.create' : 'purchasing.edit');
      return null;
    }
  };

  const onSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  /** Post return (§6's green button) — saves first, then posts. */
  const onPost = async () => {
    // BR-MD-11-14 at the confirming click, before anything is written.
    if (!(await confirmRateWarning(record?.rateWarning))) return;
    setBusy(true);
    setError(null);
    try {
      const id = await save(false);
      if (!id) return;
      if (isNew) {
        const { api } = await import('@/api');
        const response = await api.purchaseReturns.post({ params: { returnId: id }, body: {} });
        if (response.status !== 200) {
          if (response.status === 403) denyPermission('purchasing.approve');
          const body = response.body;
          const sentence =
            typeof body === 'object' && body !== null && 'message' in body
              ? String((body as { message: unknown }).message)
              : t('common.loadFailed');
          // D46: the outbound-return door raises the stock family
          // (serialsRequired, serialNotInStock, batchRequired,
          // insufficientStock…), so the body is lifted into an ApiError and
          // the seam prefers its key+params.
          setError(localizeServerMessage(t, sentence, new ApiError(response.status, sentence, body)));
          return;
        }
        void navigate(`/purchases/returns/${id}`, { replace: true });
        return;
      }
      await postReturn.mutateAsync();
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!returnId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteReturn.mutateAsync();
      void navigate('/purchases/returns');
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.delete');
    } finally {
      setBusy(false);
    }
  };

  const onSaveNotes = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateReturn.mutateAsync({ notes: notes.trim() === '' ? null : notes });
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.edit');
    } finally {
      setBusy(false);
    }
  };

  /**
   * BR-PUR-04-01 / GEN-72: cancelling a posted debit note asks WHY — the
   * ReasonBox IS the confirm (ADM-02 §8: no browser chrome). Posted only:
   * credited/refunded money un-applies first (GEN-74, the server's
   * sentence).
   */
  const onCancelPosted = async (reason: string) => {
    if (!returnId) return;
    setCancelAsk(false);
    setBusy(true);
    setError(null);
    try {
      await cancelReturn.mutateAsync(reason);
      loadedId.current = null;
    } catch (thrown) {
      handleFailure(thrown, 'purchasing.approve');
    } finally {
      setBusy(false);
    }
  };

  /** BR-PUR-04-02 / PUR-04 §12 row 4: the supplier sent money back. */
  const onRefund = async () => {
    if (!returnId || !refundAccountId) return;
    setBusy(true);
    setError(null);
    try {
      await refundReturn.mutateAsync(refundAccountId);
      loadedId.current = null;
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission('accounting.approve');
      setError(localizeServerCause(t, thrown, 'preturn.errSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Wave 1C — the LIVE totals preview. The server owns the debit note's
   * figures (it computes and stores them on save, at the bill's copied
   * prices, discount and tax rate — `preturn.pricesCopiedHint`); until then
   * the screen used to show nothing. The shared totals mirror runs the same
   * arithmetic over the rows going back, at the copied unit price and line
   * discount, under the bill's document discount and tax rate — labelled as
   * a PREVIEW, and replaced by the stored figures once a posted note stops
   * being editable. (A hook, so it sits above the loading return below.)
   */
  const taxPctForPreview = record?.taxPct ?? sourceBill?.taxPct ?? defaultTaxPct;
  const discountPctForPreview = record?.discountPct ?? sourceBill?.discountPct ?? '0';
  const previewTotals = useMemo(
    () =>
      computeTotalsMirror(
        rows
          .filter((row) => !isNotReturned(row.qty))
          .map((row) => ({ qty: row.qty, unitPrice: row.unitPrice, discountPct: row.discountPct })),
        { basis: 'pct', value: discountPctForPreview },
        { basis: 'pct', value: taxPctForPreview },
        currency,
        defaultTaxPct,
      ),
    [rows, discountPctForPreview, taxPctForPreview, currency, defaultTaxPct],
  );

  if (!isNew && loadingReturn) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  const storedTotals = record ?? null;
  const showPreview = editable && previewTotals.grandTotal !== null;
  const shownTotals = showPreview
    ? {
        qtyReturned: previewTotals.qtyTotal,
        subtotal: previewTotals.subtotal,
        taxAmount: previewTotals.taxAmount ?? '0.00',
        grandTotal: previewTotals.grandTotal ?? '0.00',
      }
    : storedTotals
      ? {
          qtyReturned: storedTotals.qtyReturned,
          subtotal: storedTotals.subtotal,
          taxAmount: storedTotals.taxAmount,
          grandTotal: storedTotals.grandTotal,
        }
      : null;

  return (
    <div className="grid gap-4">
      {/* BR-MD-11-14's question in ADM-02 §8's shared box. Rendered
          UNCONDITIONALLY, unlike the delete box below: this one is awaited, so
          a dialog that unmounted mid-question would leave the post hanging with
          no error anywhere. See `useRateWarningGate`. */}
      {rateWarningDialog}
      {/* ── Toolbar: the DBN number in the number field (BR-PUR-04-12) ───── */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('preturn.editorTitle')}</h2>
        {/* BR-SAL-00-02's naming half, on the shared chip — "Debit Note No."
            (the rule writes "Debit note No."; casing only). */}
        <DocumentNumberChip
          label={t('preturn.colNo')}
          code={record?.code ?? null}
          fallback={t('preturn.codeNew')}
        />
        <PurchaseReturnStatusChip status={status} />
        {/* §5 field 3 — the strip's pill: "Against bill · BILL-####". */}
        {billCode && linkedBillId ? (
          <Link
            to={`/purchases/bills/${linkedBillId}`}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {t('preturn.againstBill', { code: billCode })}
          </Link>
        ) : null}
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. Note
            the order: Save sits BEFORE the posted-cancel here and AFTER it on
            the bill. Two screens, two orders, for the same pair of acts —
            which is what GEN-55's "where it always is" column is about, and rule-coverage:ignore
            which the declaration makes visible instead of burying it in the
            nesting. Aligning them is the owner's call, not a refactor's. */}
        <DocumentToolbar
          header={{ kind: 'purchaseReturn', id: returnId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row — a link back to the list.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/purchases/returns',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the purchase return
              // (the spec's debit note). Saved returns only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && record ? (
                  <DocumentActions
                    request={{ kind: 'purchase_return', locale: printLocale, subjectId: record.id }}
                    payload={{ kind: 'purchase_return', data: purchaseReturnDocument(record) }}
                  />
                ) : null,
            },
            {
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && status === 'draft',
              gate: {
                permission: 'purchasing.delete',
                reasonKey: 'perm.cannot.deletePurchaseReturns',
              },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              // §6 — "Apply to supplier credit", hidden until the return is
              // posted, because there is no credit to apply before then.
              // Carries the NOTE too, so the voucher screen pre-arms this
              // credit against the oldest open bill (PUR-04 §13 step 6 —
              // "the credit is set against what is owed").
              id: 'applyCredit',
              row: null,
              label: t('preturn.applyCredit'),
              visible: status !== 'draft',
              to: `/accounting/payments/new?partnerId=${record?.partnerId ?? ''}&creditNoteId=${returnId ?? ''}`,
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isPurchaseReturnEditable(status),
              gate: {
                permission: isNew ? 'purchasing.create' : 'purchasing.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createPurchaseReturns'
                  : 'perm.cannot.editPurchaseReturns',
              },
              busy,
              onAct: () => void onSave(),
            },
            {
              // BR-PUR-04-01 — a posted note raised in error cancels; a
              // posting act, gated exactly like Post. Credited/refunded notes
              // refuse server-side (GEN-74). PART 4.6's Cancel row proper.
              id: 'cancelPosted',
              row: 'cancel',
              label: t('preturn.cancelPosted'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: status === 'posted',
              gate: {
                permission: 'purchasing.approve',
                reasonKey: 'perm.cannot.cancelPurchaseReturns',
              },
              disabled: busy,
              onAct: () => setCancelAsk(true),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && status === 'draft' ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('preturn.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* GEN-59's typed-reason box, left UNGUARDED as it already was. */}
          <ReasonBox
            open={cancelAsk}
            title={t('preturn.cancelPosted')}
            prompt={t('preturn.cancelPrompt')}
            reasonLabel={t('gen72.cancelReasonLabel')}
            confirmLabel={t('gen72.confirmCancel')}
            busy={busy}
            onConfirm={(reason) => void onCancelPosted(reason)}
            onCancel={() => setCancelAsk(false)}
          />
        </DocumentToolbar>
      </div>

      {status === 'posted' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('preturn.postedNotice')}
        </p>
      ) : null}
      {/* BR-PUR-04-02 — the other door out of `posted`: the supplier sends
          money instead of crediting (§12 row 4: DR bank / CR 2111). The
          account picks first; money in is the treasurer's act. */}
      {status === 'posted' ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          <span className="me-auto">{t('preturn.refundHint')}</span>
          <NativeSelect
            aria-label={t('preturn.refundAccount')}
            value={refundAccountId}
            onChange={(event) => setRefundAccountId(event.target.value)}
            className="w-auto"
          >
            <option value="">{t('receipts.pickAccount')}</option>
            {cashOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} — {option.name}
              </option>
            ))}
          </NativeSelect>
          <Gated permission="accounting.approve" reasonKey="perm.cannot.refundPurchaseReturns">
            <Button size="sm" variant="outline" onClick={() => void onRefund()} disabled={busy || !refundAccountId}>
              {t('preturn.refund')}
            </Button>
          </Gated>
        </div>
      ) : null}
      {status === 'refunded' ? (
        <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {t('preturn.refundedNotice')}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {t('preturn.cancelledNotice', { reason: record?.cancelReason ?? '' })}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* ── BR-PUR-04-03's picker, for the menu-entry way in ─────────────── */}
      {isNew && !sourceBillId ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('preturn.pickTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            {/* Wave 5D — the two ways a return is raised. This card IS the
                "against a bill" way; the other is its own editor on
                `?mode=direct` (a supplier and free lines, no bill). */}
            <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <span className="text-sm text-muted-foreground">{t('preturn.modeTitle')}</span>
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground">
                {t('preturn.modeAgainstBill')}
              </span>
              <Link
                to="/purchases/returns/new?mode=direct"
                className="inline-flex items-center rounded-full border border-input px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {t('preturn.modeDirect')}
              </Link>
              <span className="basis-full text-xs text-muted-foreground">
                {t('preturn.modeDirectHint')}
              </span>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dbn-supplier">{t('po.colSupplier')}</Label>
              <PartnerTypeahead
                entity="supplier"
                id="dbn-supplier"
                label={t('po.colSupplier')}
                value={pickedSupplierId || null}
                onChange={(id) => {
                  setPickedSupplierId(id ?? '');
                  setPickedBillId('');
                }}
                options={supplierPicker.options}
                selected={supplierPicker.optionFor(pickedSupplierId)}
                query={supplierPicker.query}
                onQueryChange={supplierPicker.setQuery}
                placeholder={t('preturn.pickSupplierPh')}
                emptyLabel={t('picker.noMatchingSupplier')}
                loading={supplierPicker.loading}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="dbn-bill">{t('preturn.fAgainst')}</Label>
              <Typeahead
                id="dbn-bill"
                label={t('preturn.fAgainst')}
                value={pickedBillId || null}
                onChange={(id) => setPickedBillId(id ?? '')}
                options={billPicker.options}
                selected={billPicker.optionFor(pickedBillId)}
                query={billPicker.query}
                onQueryChange={billPicker.setQuery}
                placeholder={t('preturn.pickBillPh')}
                emptyLabel={t('picker.noMatchingBill')}
                loading={billPicker.loading}
                disabled={pickedSupplierId === ''}
              />
              <p className="text-xs text-muted-foreground">{t('preturn.pickHint')}</p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* ── The details line grown by the return fields (§4) ─────────────── */}
      <Card>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-date">{t('common.date')}</Label>
            <Input
              id="dbn-date"
              type="date"
              dir="ltr"
              value={returnDate}
              onChange={(event) => setReturnDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Wave 4D — the editable rate (features/documents/rate-field). */}
          <RateField idPrefix="dbn" currency={currency} doc={record} input={rate} disabled={!editable} />
          <div className="grid gap-1.5">
            <Label>{t('preturn.fAgainst')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              <Ltr value={billCode || '—'} />
              <span className="ms-auto truncate text-muted-foreground">{partnerName}</span>
            </div>
            {/* Wave 5D: what we owe them, beside the note that lowers it. */}
            <SupplierBalanceChip
              partnerId={record?.partnerId ?? sourceBill?.partnerId ?? null}
              side="supplier"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="dbn-reason">{t('preturn.fReason')}</Label>
            <NativeSelect
              id="dbn-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value as PurchaseReturnReason)}
              disabled={!editable}
            >
              {REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(`returns.reason.${value}`)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="grid gap-1.5">
            {/* BR-PUR-04-08 — THE FLAG THE PROTOTYPE NEVER SHOWED. It decides
                whether the goods' cost offsets the supplier's credit or
                becomes a stock loss, so the consequence is written beside it
                rather than left to be discovered in the trial balance. */}
            <Label htmlFor="dbn-restocked">{t('preturn.fRestocked')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="dbn-restocked"
                checked={restocked}
                onCheckedChange={(checked) => setRestocked(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">
                {restocked ? t('preturn.restockedOn') : t('preturn.restockedOff')}
              </span>
            </label>
          </div>
          <div className="grid gap-1.5">
            {/* §15's value-only return — the exemption BR-PUR-04-05 names. */}
            <Label htmlFor="dbn-value-only">{t('preturn.fValueOnly')}</Label>
            <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
              <Checkbox
                id="dbn-value-only"
                checked={valueOnly}
                onCheckedChange={(checked) => setValueOnly(checked === true)}
                disabled={!editable}
              />
              <span className="text-muted-foreground">{t('preturn.valueOnlyHint')}</span>
            </label>
          </div>
          {reason === 'other' ? (
            <div className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
              <Label htmlFor="dbn-reason-note">{t('preturn.fReasonNote')}</Label>
              <Input
                id="dbn-reason-note"
                value={reasonNote}
                maxLength={500}
                placeholder={t('preturn.reasonNotePh')}
                onChange={(event) => setReasonNote(event.target.value)}
                disabled={!editable}
              />
            </div>
          ) : null}
          {/* Wave 5D — the header fields shared with the direct editor. */}
          <ReturnHeaderFields
            values={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            editable={editable}
            idPrefix="dbn"
          />
        </CardContent>
      </Card>

      {/* ── The returning grid: both caps per line, prices read-only ─────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('preturn.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}</TableHead>
                <TableHead className="w-24 text-end">{t('preturn.colBilled')}</TableHead>
                <TableHead className="w-28 text-end">{t('preturn.colReturned')}</TableHead>
                <TableHead className="w-28 text-end">{t('preturn.colOnHand')}</TableHead>
                <TableHead className="min-w-[130px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="w-28 text-end">{t('preturn.colSendBack')}</TableHead>
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                <TableHead className="w-28 text-end">{t('preturn.colEntryCost')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => {
                const caps = {
                  billedQty: row.billedQty,
                  alreadyReturnedQty: row.alreadyReturnedQty,
                  onHandQty: row.onHandQty === '' ? row.billedQty : row.onHandQty,
                };
                const ceiling = returnCeiling(caps, { valueOnly, currency });
                // Wave 1C: `fitsCaps` already treats blank/zero as "not returned".
                const overCap = !fitsCaps(row.qty, caps, { valueOnly, currency });
                const unit = unitOf(row);
                return (
                  <React.Fragment key={row.billLineNo}>
                    <TableRow className="odd:bg-muted/30">
                      <TableCell className="text-center font-mono text-muted-foreground">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.description}
                        {row.itemCode ? (
                          <span className="ms-2 font-mono text-xs text-muted-foreground">
                            <bdi dir="ltr">{row.itemCode}</bdi>
                          </span>
                        ) : null}
                      </TableCell>
                      {/* `N-4` on PUR-04's three caps. All three are in the
                          same unit — a bill line has no unit of its own and
                          `onHandQty` is a shelf balance — which is what makes
                          them comparable; they just never said which. */}
                      <TableCell className="text-end">
                        <CapQty unit={unit} value={row.billedQty} />
                      </TableCell>
                      <TableCell className="text-end text-muted-foreground">
                        <CapQty unit={unit} value={row.alreadyReturnedQty} />
                      </TableCell>
                      {/* BR-PUR-04-05's ceiling, and where it is — the shelf
                          the goods actually came in to. */}
                      <TableCell className="text-end text-muted-foreground">
                        {row.onHandQty === '' ? (
                          <bdi dir="ltr" className="font-mono">
                            —
                          </bdi>
                        ) : (
                          <CapQty unit={unit} value={row.onHandQty} />
                        )}
                        {row.warehouseName ? (
                          <div className="text-[11px] font-sans">{row.warehouseName}</div>
                        ) : null}
                      </TableCell>
                      {/* Wave 5D — where THIS line's goods leave from. Blank
                          keeps the shelf they came in to; a service or
                          free-text line has nothing to move. */}
                      <TableCell>
                        {row.itemId ? (
                          <NativeSelect
                            aria-label={t('preturn.cellWarehouse', { line: index + 1 })}
                            className="h-9 text-sm"
                            value={row.warehouseId}
                            onChange={(event) => setRowWarehouse(row.billLineNo, event.target.value)}
                            disabled={!editable || valueOnly}
                          >
                            <option value="">{row.warehouseName || t('sales.pickWarehouse')}</option>
                            {activeWarehouses.map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                          </NativeSelect>
                        ) : (
                          <span className="px-2 text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className={editable ? undefined : 'text-end'}>
                        {/* A POSTED debit note is a record: the goods have
                            gone and the box can never be typed into again, so
                            it reads as the quantity it is. A DRAFT keeps its
                            box even at a zero ceiling — disabled, not done. */}
                        {editable ? (
                          <CellInput
                            mono
                            dir="ltr"
                            inputMode="decimal"
                            aria-label={t('preturn.cellSendBack', { line: index + 1 })}
                            className={cn(overCap && 'border-destructive text-destructive')}
                            value={row.qty}
                            placeholder="0"
                            onChange={(event) => setRowQty(row.billLineNo, event.target.value)}
                            disabled={ceiling === '0'}
                          />
                        ) : (
                          <CapQty unit={unit} value={row.qty === '' ? '0' : row.qty} />
                        )}
                        {/* The decimals, not a second symbol: this note sits
                            in the cell immediately after On hand, which
                            already prints the unit (INV-06's balance call). */}
                        <div className="pe-2 text-end text-[11px] text-muted-foreground">
                          {t('preturn.maxMore', {
                            qty: unit === null ? ceiling : formatQty(ceiling, unit.precision),
                          })}
                        </div>
                      </TableCell>
                      {/* BR-PUR-04-06: copied and read-only. */}
                      <TableCell className="text-end">
                        <Amount value={row.unitPrice} currency={currency} />
                      </TableCell>
                      {/* BR-PUR-04-07: what stock will actually be relieved at. */}
                      <TableCell className="text-end font-mono text-xs text-muted-foreground">
                        <bdi dir="ltr">
                          {row.entryUnitCost === '' ? '—' : trimQty(row.entryUnitCost)}
                        </bdi>
                      </TableCell>
                    </TableRow>
                    {/* A value-only return moves nothing, so nothing is named. */}
                    {!valueOnly ? (
                      <TrackingRows
                        blocks={blocksFor(row)}
                        text={row.tracking}
                        onChange={(key, value) => setRowTracking(row.billLineNo, key, value)}
                        disabled={!editable}
                        columns={9}
                        line={index + 1}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </TableBody>
            {shownTotals ? (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={9} className="text-end text-sm">
                    <span className="text-muted-foreground">{t('orders.netTaxTotal')}</span>{' '}
                    <Amount value={shownTotals.subtotal} currency={currency} /> {' / '}
                    <Amount value={shownTotals.taxAmount} currency={currency} /> {' / '}
                    <Amount
                      value={shownTotals.grandTotal}
                      currency={currency}
                      className="font-bold"
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            ) : null}
          </Table>
          <p className="mt-2 text-xs text-muted-foreground">{t('preturn.pricesCopiedHint')}</p>
        </CardContent>
      </Card>

      {/* ── Notes + the posting rail ─────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader>
            <CardTitle>{t('sales.notes')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <Textarea
              aria-label={t('sales.notes')}
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!editable && !(status !== 'draft' && canEditNotes)}
            />
            {status !== 'draft' ? (
              <Gated
                permission="purchasing.edit"
                reasonKey="perm.cannot.editNotes"
                className="justify-self-start"
              >
                <Button variant="outline" onClick={() => void onSaveNotes()} disabled={busy}>
                  {t('sales.saveNotes')}
                </Button>
              </Gated>
            ) : null}
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardContent className="grid gap-3">
            {shownTotals ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span>{t('preturn.qtyReturned')}</span>
                  <bdi dir="ltr" className="font-mono">
                    {shownTotals.qtyReturned}
                  </bdi>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>{t('sales.subtotal')}</span>
                  <Amount value={shownTotals.subtotal} currency={currency} />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>{t('sales.vat')}</span>
                  <Amount value={shownTotals.taxAmount} currency={currency} />
                </div>
                {/* §5 field 7 — "the most prominent number on the strip". */}
                <div className="flex items-center justify-between rounded-lg bg-primary px-4 py-3 text-primary-foreground">
                  <span className="text-sm font-medium">{t('preturn.debitTotal')}</span>
                  <span className="flex items-baseline gap-2">
                    {/* Wave 5D: the currency CODE beside the figure (GEN-108) —
                        a debit in EUR must not be read as the company's own money. */}
                    <Ltr value={currency} className="text-xs font-medium opacity-80" />
                    <span className="text-lg font-bold">
                      <Amount value={shownTotals.grandTotal} currency={currency} />
                    </span>
                  </span>
                </div>
                {/* Wave 1C: a preview is labelled as one until the draft is saved. */}
                {showPreview ? (
                  <p className="text-xs text-muted-foreground">{t('preturn.totalsPreview')}</p>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t('preturn.totalsOnSave')}</p>
            )}

            {/* The third of BR-SAL-04-09's three purchase strips. The debit
                note keeps its OWN rate, frozen when it posts — not the rate of
                the bill it reverses, which is what makes the pair of them
                capable of producing an exchange difference at all. Hidden
                entirely for a base-currency return (GEN-116). */}
            <FxPanel doc={record} />

            {/* §6 — "Post return", behind purchasing.approve. */}
            {status === 'draft' ? (
              <Gated
                permission="purchasing.approve"
                reasonKey="perm.cannot.postPurchaseReturns"
                className="w-full"
              >
                <Button
                  className="h-[46px] w-full bg-emerald-700 text-white hover:bg-emerald-800"
                  onClick={() => void onPost()}
                  disabled={busy || requestLines.length === 0}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden /> : <Send aria-hidden />}
                  {t('preturn.postReturn')}
                </Button>
              </Gated>
            ) : null}
            {/* §5 field 10 / ERR-PUR-04-05, word for word. */}
            {status === 'draft' && !canPost ? (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                {t('preturn.approvalBanner')}
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document (the supplier's credit note, as a scan). Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="purchase_return" entityId={returnId} />
    </div>
  );
}
